import { Log } from '@/parsing/Log';
import { LogEntry, LogEntryType } from '@/parsing/LogEntry';

import { TimelineMarker, TimelineRow, TimelineRowGroup } from '../../TimelineData';
import { addTimeSpanMarker } from '../addTimeSpanMarker';
import { obtainFreeRowForMarker } from '../obtainFreeRowForMarker';
import { TimelineGeneratorContext } from '../TimelineGeneratorContext';

export function generateIosAppStateGroupFromLog(
  log: Log,
  ctx: TimelineGeneratorContext,
): TimelineRowGroup {
  const appIsActiveRow: TimelineRow = { markers: [] };
  const globalProgressSpinnerRow: TimelineRow = { markers: [] };
  const rows: TimelineRow[] = [];

  let appBecameActiveEntry: LogEntry | undefined;
  let appResignActiveEntry: LogEntry | undefined;

  let globalProgressEntryStart: LogEntry | undefined;

  for (const entry of log.entries) {
    if (entry.entryType !== LogEntryType.Generic) {
      continue;
    }
    if (entry.extra.generic?.module !== 'GLOBAL') {
      continue;
    }

    const tooltipContent = entry.content;
    let textOnMarker = '';
    let fillColor = '';

    if (entry.content.startsWith('App finished launching')) {
      textOnMarker = 'Launch';
      fillColor = '#b7cef8';
    } else if (entry.content.startsWith('Application became active')) {
      textOnMarker = 'Active';
      fillColor = '#3eff37';
      appBecameActiveEntry = entry;
    } else if (entry.content.startsWith('Application will resign active')) {
      textOnMarker = 'Will resign active';
      fillColor = '#effc4c';
      if (appBecameActiveEntry) {
        appResignActiveEntry = entry;
      }
    } else if (entry.content.startsWith('Application will enter foreground')) {
      textOnMarker = 'Will enter foreground';
      fillColor = '#f69e38';
      if (appBecameActiveEntry) {
        appResignActiveEntry = entry;
      }
    } else if (entry.content.startsWith('Show Global Progress spinner')) {
      globalProgressEntryStart = entry;
      continue;
    } else if (entry.content.startsWith('Hide Global Progress spinner')) {
      if (globalProgressEntryStart) {
        addGlobalSpinnerTimeSpanMarker({
          startEntry: globalProgressEntryStart,
          endEntry: entry,
          endTimeMs: entry.date.getTime(),
          ctx,
          row: globalProgressSpinnerRow,
        });

        globalProgressEntryStart = undefined;
      }

      continue;
    } else {
      continue;
    }

    const marker: TimelineMarker = {
      markerId: ctx.nextMarkerId,
      logEntry: entry,
      startTimeMs: entry.date.getTime(),
      endTimeMs: undefined,
      fillColor,
      textOnMarker,
      textOnMarkerColor: undefined,
      tooltipContent,
    };
    ctx.nextMarkerId += 1;

    const row = obtainFreeRowForMarker(rows, marker);
    row.markers.push(marker);

    if (appBecameActiveEntry && appResignActiveEntry) {
      addAppIsActiveSpanTimeMarker({
        startEntry: appBecameActiveEntry,
        endEntry: appResignActiveEntry,
        endTimeMs: appResignActiveEntry.date.getTime(),
        appIsActiveRow,
        ctx,
      });

      appBecameActiveEntry = undefined;
      appResignActiveEntry = undefined;
    }
  }

  const lastEntryTimeMs = log.entries[log.entries.length - 1].date.getTime();

  if (globalProgressEntryStart) {
    addGlobalSpinnerTimeSpanMarker({
      startEntry: globalProgressEntryStart,
      endEntry: undefined,
      endTimeMs: lastEntryTimeMs,
      ctx,
      row: globalProgressSpinnerRow,
    });
  }

  if (appBecameActiveEntry) {
    addAppIsActiveSpanTimeMarker({
      startEntry: appBecameActiveEntry,
      endEntry: undefined,
      endTimeMs: lastEntryTimeMs,
      appIsActiveRow,
      ctx,
    });
    appBecameActiveEntry = undefined;
  }

  const rowGroup: TimelineRowGroup = {
    name: 'Application state',
    rows,
    expanded: true,
  };

  if (globalProgressSpinnerRow.markers.length > 0) {
    rowGroup.rows.splice(0, 0, globalProgressSpinnerRow);
  }
  if (appIsActiveRow.markers.length > 0) {
    rowGroup.rows.splice(0, 0, appIsActiveRow);
  }

  return rowGroup;
}

function addAppIsActiveSpanTimeMarker(args: {
  startEntry: LogEntry;
  endEntry: LogEntry | undefined;
  endTimeMs: number;
  appIsActiveRow: TimelineRow;
  ctx: TimelineGeneratorContext;
}) {
  addTimeSpanMarker({
    startEntry: args.startEntry,
    endEntry: args.endEntry,
    endTimeMs: args.endTimeMs,
    row: args.appIsActiveRow,
    ctx: args.ctx,
    fillColor: '#ecc9f3',
    textOnMarker: `App is active`,
    textOnMarkerColor: '#571d5b',
    tooltipContent: `Application was active during this time span`,
  });
}

function addGlobalSpinnerTimeSpanMarker(args: {
  startEntry: LogEntry;
  endEntry: LogEntry | undefined;
  endTimeMs: number;
  row: TimelineRow;
  ctx: TimelineGeneratorContext;
}) {
  addTimeSpanMarker({
    startEntry: args.startEntry,
    endEntry: args.endEntry,
    endTimeMs: args.endTimeMs,
    row: args.row,
    ctx: args.ctx,
    fillColor: '#f18c4d',
    textOnMarker: `Fullscreen spinner`,
    textOnMarkerColor: '#5a1b0c',
    tooltipContent: `Global spinner is shown during this time span`,
  });
}
